import React, { useState, useEffect } from "react";
import HeaderBar from "./HeaderBar";
import {
  Layout,
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Upload,
  Typography,
  message,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "./Sidebar";
import { BASE_URL } from "./config";
import Footer from "./Footer";

const { Header, Content, Sider } = Layout;
const { Option } = Select;

const UpdateProfile = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [form] = Form.useForm();
  const [user, setUser] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();

  // ✅ Load user from localStorage
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (storedUser) setUser(storedUser);
  }, []);

  // ✅ Fetch profile data by ID
  useEffect(() => {
    if (!id) return;

    const fetchProfile = async () => {
      try {
        const res = await fetch(`${BASE_URL}/getProfile.php?id=${id}`);
        const data = await res.json();

        if (!data.success) return message.error("Profile not found");

        const record = data.data;

        form.setFieldsValue({
          fullName: record.username || "",
          display_name: record.display_name || "",
          designation: record.designation || "",
          workEmail: record.email || "",
          mobile: record.phone || "",
          whatsapp: record.whatsapp || "",
          businessName: record.businessName || "",
          officialEmail: record.officialEmail || "",
          website: record.company_website || "",
          address: record.address || "",
          city: record.city || "",
          pincode: record.pincode || "",
          state: record.state || "",
          country: record.country || "",
          businessType: record.businessType || "",
          pan: record.pan || "",
          gst: record.gst || "",
        });

        // ✅ Helper to preload uploaded files
        const preloadFile = (path, uid) =>
          path
            ? [
                {
                  uid,
                  name: path.split("/").pop(),
                  status: "done",
                  url: `${BASE_URL}/${path}`,
                },
              ]
            : [];

        // ✅ Set file fields and logo name separately
        const logoName = record.profileImage
          ? record.profileImage.split("/").pop()
          : "";

        form.setFieldsValue({
          profileImage: preloadFile(record.profileImage, "-1"),
          panCard: preloadFile(record.panCard, "-2"),
          gstattach: preloadFile(record.gstattach, "-3"),
          logo: logoName,
        });
      } catch (err) {
        console.error("Fetch error:", err);
        message.error("Failed to load profile data");
      }
    };

    fetchProfile();
  }, [id, form]);

  // ✅ Handle Submit
// ✅ Handle Submit
const handleSubmit = async (values) => {
  const formData = new FormData();
  formData.append("id", id);

  Object.entries(values).forEach(([key, value]) => {
    if (Array.isArray(value) && value[0]?.originFileObj) {
      formData.append(key, value[0].originFileObj);
    } else if (!Array.isArray(value)) {
      formData.append(key, value);
    }
  });

  try {
    const res = await fetch(`${BASE_URL}/updateProfile.php`, {
      method: "POST",
      body: formData,
    });
    const data = await res.json();

    if (data.success) {
      message.success("Profile updated successfully!");

      // ✅ Automatically refresh login info (to get new logo)
      const storedUser = JSON.parse(localStorage.getItem("user"));
      if (storedUser && storedUser.email && storedUser.password) {
        const loginRes = await fetch(`${BASE_URL}/api_login.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: storedUser.email,
            password: storedUser.password, // 🔑 must store password in localStorage temporarily
          }),
        });

        const loginData = await loginRes.json();

        if (loginData.success) {
          localStorage.setItem("user", JSON.stringify(loginData));
          setUser(loginData);
          message.info("User data refreshed with new logo!");
        } else {
          message.warning("Profile updated but failed to refresh login data.");
        }
      }
    } else {
      message.error("Failed to update profile");
    }
  } catch (err) {
    console.error("Update error:", err);
    message.error("Error updating profile");
  }
};


  const colResponsive = { xs: 24, sm: 24, md: 12, lg: 8, xl: 8 };

  return (
    <Layout style={{ minHeight: "100vh", fontFamily: "Poppins, sans-serif" }}>
      <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          breakpoint="lg"
          collapsedWidth="80"
        >
          <Sidebar collapsed={collapsed} user={user} />
        </Sider>

      <Layout>
        <HeaderBar title="Update Profile" />

        <Content style={{ margin: 5 }}>
          <div
            style={{
              padding: "10px",
              background: "#fff",
              borderRadius: 10,
              maxWidth: 1200,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <Form form={form} layout="vertical" onFinish={handleSubmit}>
              {/* 🧾 Personal Info */}
              <Row gutter={16}>
                <Col {...colResponsive}>
                  <Form.Item
                    label="Full Name"
                    name="fullName"
                    rules={[{ required: true }]}
                  >
                    <Input placeholder="Enter Full Name" />
                  </Form.Item>
                </Col>
                <Col {...colResponsive}>
                  <Form.Item label="Display Name" name="display_name">
                    <Input placeholder="Enter Display Name" />
                  </Form.Item>
                </Col>
                <Col {...colResponsive}>
                  <Form.Item label="Designation" name="designation">
                    <Input placeholder="Enter Designation" />
                  </Form.Item>
                </Col>
              </Row>

              {/* 📞 Contact Info */}
              <Row gutter={16}>
                <Col {...colResponsive}>
                  <Form.Item label="Email" name="workEmail">
                    <Input placeholder="Enter Email" />
                  </Form.Item>
                </Col>
                <Col {...colResponsive}>
                  <Form.Item
                    label="Mobile"
                    name="mobile"
                    rules={[{ required: true }]}
                  >
                    <Input addonBefore="+91" placeholder="Enter Mobile" />
                  </Form.Item>
                </Col>
                <Col {...colResponsive}>
                  <Form.Item label="WhatsApp" name="whatsapp">
                    <Input addonBefore="+91" placeholder="Enter WhatsApp" />
                  </Form.Item>
                </Col>
              </Row>

              {/* 🏢 Business Info */}
              <Row gutter={16}>
                <Col {...colResponsive}>
                  <Form.Item label="Business Name" name="businessName">
                    <Input placeholder="Enter Business Name" />
                  </Form.Item>
                </Col>
                <Col {...colResponsive}>
                  <Form.Item label="Official Email" name="officialEmail">
                    <Input placeholder="Enter Official Email" />
                  </Form.Item>
                </Col>
                <Col {...colResponsive}>
                  <Form.Item label="Website" name="website">
                    <Input placeholder="Enter Website URL" />
                  </Form.Item>
                </Col>
              </Row>

              {/* 🏠 Address */}
              <Row gutter={16}>
                <Col {...colResponsive}>
                  <Form.Item label="Address" name="address">
                    <Input placeholder="Enter Address" />
                  </Form.Item>
                </Col>
                <Col {...colResponsive}>
                  <Form.Item label="City" name="city">
                    <Input placeholder="Enter City" />
                  </Form.Item>
                </Col>
                <Col {...colResponsive}>
                  <Form.Item label="Pin Code" name="pincode">
                    <Input placeholder="Enter Pincode" />
                  </Form.Item>
                </Col>
              </Row>

              {/* 🌍 Location Info */}
              <Row gutter={16}>
                <Col {...colResponsive}>
                  <Form.Item label="State" name="state">
                    <Input placeholder="Enter State" />
                  </Form.Item>
                </Col>
                <Col {...colResponsive}>
                  <Form.Item label="Country" name="country">
                    <Select placeholder="Select Country">
                      <Option value="India">India</Option>
                      <Option value="USA">USA</Option>
                      <Option value="UK">UK</Option>
                      <Option value="Other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col {...colResponsive}>
                  <Form.Item label="Business Type" name="businessType">
                    <Select placeholder="Select Business Type">
                      <Option value="IT">IT</Option>
                      <Option value="Consulting">Consulting</Option>
                      <Option value="Education">Education</Option>
                      <Option value="Other">Other</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {/* 📄 Uploads */}
              <Row gutter={16}>
                <Col {...colResponsive}>
                  <Form.Item
                    label="Profile Image"
                    name="profileImage"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => {
                      const fileList = e?.fileList || [];
                      if (fileList.length > 0) {
                        // ✅ Properly update logo field after upload
                        const fileName = fileList[0]?.name || "";
                        setTimeout(() => {
                          form.setFieldsValue({ logo: fileName });
                        }, 0);
                      }
                      return fileList;
                    }}
                  >
                    <Upload beforeUpload={() => false} accept=".jpg,.jpeg,.png">
                      <Button icon={<UploadOutlined />}>Choose File</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                {/* ✅ Hidden logo field */}
                <Form.Item name="logo" style={{ display: "none" }}>
                  <Input type="hidden" />
                </Form.Item>

                <Col {...colResponsive}>
                  <Form.Item label="PAN" name="pan">
                    <Input placeholder="Enter PAN" />
                  </Form.Item>
                </Col>

                <Col {...colResponsive}>
                  <Form.Item
                    label="PAN Card"
                    name="panCard"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => e?.fileList}
                  >
                    <Upload
                      beforeUpload={() => false}
                      accept=".jpg,.jpeg,.png,.pdf"
                    >
                      <Button icon={<UploadOutlined />}>Choose File</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col {...colResponsive}>
                  <Form.Item label="GST No" name="gst">
                    <Input placeholder="Enter GST No" />
                  </Form.Item>
                </Col>

                <Col {...colResponsive}>
                  <Form.Item
                    label="GST Attachment"
                    name="gstattach"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => e?.fileList}
                  >
                    <Upload
                      beforeUpload={() => false}
                      accept=".jpg,.jpeg,.png,.pdf"
                    >
                      <Button icon={<UploadOutlined />}>Choose File</Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>

              <div style={{ textAlign: "center", marginTop: 20 }}>
                <Button type="primary" htmlType="submit" size="large">
                  Update Profile
                </Button>
              </div>
            </Form>
          </div>
        </Content>
        <Footer user={user}/>
      </Layout>
    </Layout>
  );
};

export default UpdateProfile;
